import { z } from "zod";
/**
 * Google Agenda e Google Planilhas.
 *
 * Os contratos moram juntos porque as duas integrações dividem a mesma
 * conexão OAuth: uma conta Google por empresa, com os escopos das duas.
 */
// ── Planilha ────────────────────────────────────────────────────────────────
/**
 * Id de planilha do Google: letras, dígitos, `-` e `_`, com dezenas de
 * caracteres. O piso de 20 é folgado (os ids reais têm 44) e serve só para
 * recusar lixo óbvio antes de gastar uma chamada ao Google.
 */
const ID_PLANILHA = /^[A-Za-z0-9_-]{20,}$/;
/**
 * Extrai o id da planilha de uma URL do Google Planilhas (ou devolve o próprio
 * id, se já vier assim).
 *
 * Existe porque quem configura cola o que está na barra do navegador —
 * `https://docs.google.com/spreadsheets/d/<id>/edit#gid=0` —, e pedir "o id"
 * faria metade das pessoas colar a URL inteira e a outra metade colar o pedaço
 * errado. Devolve `null` quando não reconhece nada.
 */
export function extractSpreadsheetId(input) {
    const texto = (input ?? "").trim();
    if (!texto)
        return null;
    const daUrl = /\/spreadsheets\/(?:u\/\d+\/)?d\/([A-Za-z0-9_-]+)/.exec(texto);
    if (daUrl?.[1] && ID_PLANILHA.test(daUrl[1]))
        return daUrl[1];
    // `?id=` aparece em links antigos de compartilhamento do Drive.
    const daQuery = /[?&]id=([A-Za-z0-9_-]+)/.exec(texto);
    if (daQuery?.[1] && ID_PLANILHA.test(daQuery[1]))
        return daQuery[1];
    return ID_PLANILHA.test(texto) ? texto : null;
}
/** Link de abertura de uma planilha a partir do id. */
export function spreadsheetUrl(spreadsheetId) {
    return `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`;
}
export const sheetColumnSchema = z.object({
    header: z.string().trim().min(1, "Dê um nome à coluna").max(100),
    /** Texto com variáveis — `{{contact.fullName}}`, `{{lead.source}}`. */
    value: z.string().max(500),
});
/**
 * Variáveis oferecidas no campo "valor" da coluna.
 *
 * São as que o motor de automação já monta no contexto (`buildContext`):
 * lead, contato, negócio e o evento que disparou. Datas saem formatadas em
 * dd/mm/aaaa hh:mm no fuso da empresa — o `Date` cru viraria texto em inglês.
 */
export const SHEET_TEMPLATE_VARIABLES = [
    { value: "{{agora}}", label: "Data e hora de agora" },
    { value: "{{contact.fullName}}", label: "Nome completo" },
    { value: "{{contact.firstName}}", label: "Primeiro nome" },
    { value: "{{contact.phoneNormalized}}", label: "Telefone" },
    { value: "{{contact.email}}", label: "E-mail" },
    { value: "{{contact.city}}", label: "Cidade" },
    { value: "{{lead.source}}", label: "Origem do lead" },
    { value: "{{lead.sourceDetail}}", label: "Detalhe da origem" },
    { value: "{{lead.status}}", label: "Status do lead" },
    { value: "{{lead.temperature}}", label: "Temperatura" },
    { value: "{{lead.leadScore}}", label: "Pontuação" },
    { value: "{{lead.createdAt}}", label: "Data de entrada do lead" },
    { value: "{{deal.title}}", label: "Negócio" },
    { value: "{{deal.stageName}}", label: "Etapa do funil" },
    { value: "{{deal.value}}", label: "Valor do negócio" },
    { value: "{{event.name}}", label: "Evento que disparou" },
];
/** Colunas da "planilha de leads" — o caso comum, pronto para usar. */
export const DEFAULT_LEAD_SHEET_COLUMNS = [
    { header: "Data", value: "{{agora}}" },
    { header: "Nome", value: "{{contact.fullName}}" },
    { header: "Telefone", value: "{{contact.phoneNormalized}}" },
    { header: "E-mail", value: "{{contact.email}}" },
    { header: "Origem", value: "{{lead.source}}" },
    { header: "Detalhe da origem", value: "{{lead.sourceDetail}}" },
    { header: "Etapa", value: "{{deal.stageName}}" },
];
// ── Agenda ──────────────────────────────────────────────────────────────────
export const googleCalendarLinkSchema = z.object({
    /** `null` = agenda única, para quem não tem agenda própria. */
    professionalId: z.string().uuid().nullable(),
    calendarId: z.string().trim().min(1, "Escolha a agenda").max(300),
    calendarName: z.string().trim().max(300).nullable().optional(),
    blockBusy: z.boolean().default(false),
});
export const saveGoogleCalendarLinksSchema = z.object({
    links: z
        .array(googleCalendarLinkSchema)
        .max(200)
        .superRefine((links, ctx) => {
        // Uma linha por profissional (e uma só agenda única): duas agendas para
        // a mesma pessoa deixariam a sincronização sem saber onde escrever.
        const vistos = new Set();
        links.forEach((l, i) => {
            const chave = l.professionalId ?? "__unica__";
            if (vistos.has(chave)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [i, "professionalId"],
                    message: "Cada profissional tem uma agenda só",
                });
            }
            vistos.add(chave);
        });
    }),
});
export const createLeadSheetSchema = z.object({
    title: z.string().trim().min(2).max(120).optional(),
});
//# sourceMappingURL=google.js.map